import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from '@radix-ui/react-icons';
import type { RowData } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PAGE_SIZES, type ListSearch } from '@/lib/url-state';
import type { DataTableController } from './use-data-table';

export function DataTablePagination<TData extends RowData, TSearch extends ListSearch>({
  controller,
  className,
}: {
  controller: DataTableController<TData, TSearch>;
  className?: string;
}) {
  const { table, total } = controller;
  const { pageIndex, pageSize } = table.state.pagination;
  const pageCount = Math.max(1, table.getPageCount());
  const from = total ? pageIndex * pageSize + 1 : 0;
  const to = Math.min(total ?? 0, (pageIndex + 1) * pageSize);
  const selected = controller.selectedRows.length;

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm', className)}>
      <p className="text-muted-foreground tabular-nums" aria-live="polite">
        {selected > 0 && `${formatNumber(selected)} selected · `}
        {total === undefined
          ? 'Loading…'
          : total === 0
            ? 'No rows'
            : `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)}`}
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground" id={`${controller.id}-page-size`}>
            Rows per page
          </span>
          <Select value={String(pageSize)} onValueChange={(v) => table.setPageSize(Number(v))}>
            <SelectTrigger size="sm" className="w-18" aria-labelledby={`${controller.id}-page-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="tabular-nums">
          Page {formatNumber(pageIndex + 1)} of {formatNumber(pageCount)}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="First page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.firstPage()}
          >
            <DoubleArrowLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Previous page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Next page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Last page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.lastPage()}
          >
            <DoubleArrowRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}
